/**
 * Generic in-memory entity store used by mock repositories.
 *
 * All entities are identified by numeric `id`. The store mirrors typical REST
 * collection semantics; mock repositories thin-wrap this and add simulated
 * latency via `mockResponse`, so swapping a feature to the real HTTP repository
 * is a one-line provider change in `app.config.ts`.
 */
export class EntityStore<T extends { id: number }> {
  private readonly items = new Map<number, T>();
  private nextId = 1;

  constructor(seed: readonly T[] = []) {
    for (const entity of seed) {
      this.items.set(entity.id, entity);
      if (entity.id >= this.nextId) this.nextId = entity.id + 1;
    }
  }

  /** Allocate the next monotonically increasing id. */
  allocateId(): number {
    return this.nextId++;
  }

  list(): T[] {
    return [...this.items.values()];
  }

  get(id: number): T | undefined {
    return this.items.get(id);
  }

  /** Insert or replace by id. Returns the stored entity. */
  upsert(entity: T): T {
    this.items.set(entity.id, entity);
    if (entity.id >= this.nextId) this.nextId = entity.id + 1;
    return entity;
  }

  /** Apply a partial patch to an existing entity. Returns the updated entity, or `undefined` if not found. */
  patch(id: number, changes: Partial<T>): T | undefined {
    const existing = this.items.get(id);
    if (!existing) return undefined;
    const next = { ...existing, ...changes } as T;
    this.items.set(id, next);
    return next;
  }

  remove(id: number): boolean {
    return this.items.delete(id);
  }

  size(): number {
    return this.items.size;
  }

  clear(): void {
    this.items.clear();
    this.nextId = 1;
  }
}
